import { useState, useRef, useEffect, ComponentProps } from 'react';

import { GripVerticalIcon } from 'lucide-react';
import * as ResizablePrimitive from 'react-resizable-panels';

import { cn } from '@/lib/utils';

const HOVER_DELAY = 100;

/**
 * Resizable panel group container that manages multiple resizable panels
 *
 * @param {string} className - Additional CSS classes
 * @returns {JSX.Element} ResizablePanelGroup component
 */
function ResizablePanelGroup({
  className,
  ...props
}: ComponentProps<typeof ResizablePrimitive.PanelGroup>) {
  return (
    <ResizablePrimitive.PanelGroup
      data-slot="resizable-panel-group"
      className={cn('flex h-full w-full data-[panel-group-direction=vertical]:flex-col', className)}
      {...props}
    />
  );
}

/**
 * Individual resizable panel within a ResizablePanelGroup
 *
 * @returns {JSX.Element} ResizablePanel component
 */
function ResizablePanel({ ...props }: ComponentProps<typeof ResizablePrimitive.Panel>) {
  return <ResizablePrimitive.Panel data-slot="resizable-panel" {...props} />;
}

/**
 * Handle to resize panels. Hover effect activates only with intentional hovering.
 *
 * @param {boolean} withHandle - Whether to show the visual grip handle
 * @param {string} className - Additional CSS classes
 * @returns {JSX.Element} ResizableHandle component
 */
function ResizableHandle({
  withHandle,
  className,
  ...props
}: ComponentProps<typeof ResizablePrimitive.PanelResizeHandle> & { withHandle?: boolean }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handlePointerDown = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovered(false);
    setIsDragging(true);
  };

  const handlePointerUp = () => setIsDragging(false);

  const handlePointerEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(true);
    }, HOVER_DELAY);
  };

  const handlePointerLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(false);
  };

  return (
    <ResizablePrimitive.PanelResizeHandle
      data-slot="resizable-handle"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      className={cn(
        'relative flex w-px cursor-col-resize items-center justify-center bg-border transition-all duration-[250ms] ease-out before:pointer-events-auto before:absolute before:inset-0 before:-mx-[4.5px] before:w-[10px] focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-hidden data-[panel-group-direction=vertical]:h-px data-[panel-group-direction=vertical]:w-full data-[panel-group-direction=vertical]:cursor-row-resize data-[panel-group-direction=vertical]:before:-my-[4.5px] data-[panel-group-direction=vertical]:before:h-[10px] [&[data-panel-group-direction=vertical]>div]:rotate-90',
        // Hover styles (only when intentionally hovered and not dragging)
        isHovered &&
          !isDragging &&
          '-mx-[0.5px] w-[2px] bg-accent data-[panel-group-direction=vertical]:-my-[0.5px] data-[panel-group-direction=vertical]:h-[2px] data-[panel-group-direction=vertical]:w-full',
        // Dragging styles (forced accent when dragging)
        isDragging &&
          '-mx-[0.5px] w-[2px] bg-accent data-[panel-group-direction=vertical]:-my-[0.5px] data-[panel-group-direction=vertical]:h-[2px] data-[panel-group-direction=vertical]:w-full',
        className
      )}
      {...props}
    >
      {withHandle && (
        <div className="z-10 flex h-4 w-3 items-center justify-center rounded-xs border bg-border">
          <GripVerticalIcon className="size-2.5" />
        </div>
      )}
    </ResizablePrimitive.PanelResizeHandle>
  );
}

export { ResizablePanelGroup, ResizablePanel, ResizableHandle };
